import React, { useEffect, useState, useRef, useCallback } from 'react';
import { api } from '../config/api';
import './heroslider.css';

const SLIDE_DURATION_MS = 7000;

const HeroSlider = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [useFallback, setUseFallback] = useState(false);
  const [fallbackIndex, setFallbackIndex] = useState(0);

  const carouselRef = useRef(null);
  const slickInitialized = useRef(false);
  const mountedRef = useRef(true);

  const [showTrailer, setShowTrailer] = useState(false);
  const [currentTrailer, setCurrentTrailer] = useState('');

  const convertToEmbedUrl = useCallback((url) => {
    if (!url) return '';
    try {
      const u = new URL(url);
      let videoId = '';
      if (u.hostname.includes('youtu.be')) {
        videoId = u.pathname.split('/').filter(Boolean)[0] || '';
      } else if (u.pathname.startsWith('/embed/')) {
        videoId = u.pathname.split('/')[2] || '';
      } else if (u.searchParams.get('v')) {
        videoId = u.searchParams.get('v');
      }
      if (!videoId) return '';
      return `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1&playsinline=1`;
    } catch {
      return '';
    }
  }, []);

  const safeSlickCall = useCallback((method, ...args) => {
    if (!carouselRef.current || !window.$ || !slickInitialized.current) return;
    try {
      const $c = window.$(carouselRef.current);
      if (!$c.hasClass('slick-initialized')) return;
      $c.slick(method, ...args);
    } catch (e) {
      /* ignore */
    }
  }, []);

  const handleArrowEnter = useCallback(() => {
    if (showTrailer) return;
    safeSlickCall('slickPause');
  }, [safeSlickCall, showTrailer]);

  const handleArrowLeave = useCallback(() => {
    if (showTrailer) return;
    safeSlickCall('slickPlay');
  }, [safeSlickCall, showTrailer]);

  const goPrev = useCallback(() => {
    safeSlickCall('slickPrev');
  }, [safeSlickCall]);

  const goNext = useCallback(() => {
    safeSlickCall('slickNext');
  }, [safeSlickCall]);

  useEffect(() => {
    mountedRef.current = true;
    const fetchBanners = async () => {
      try {
        let fetchedBanners = await api.getBanners('Hero');
        if (!fetchedBanners || fetchedBanners.length === 0) {
          fetchedBanners = await api.getBanners();
        }
        fetchedBanners = fetchedBanners.filter(b =>
          ['hero', 'home_hero'].includes((b.position || '').toLowerCase())
        );
        if (mountedRef.current) {
          setBanners(fetchedBanners || []);
          setLoading(false);
        }
      } catch {
        if (mountedRef.current) {
          setBanners([]);
          setLoading(false);
        }
      }
    };
    fetchBanners();
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const waitForSlick = useCallback(() => {
    return new Promise((resolve) => {
      const start = Date.now();
      const check = () => {
        if (window.$ && window.$.fn && window.$.fn.slick) {
          resolve(true);
          return;
        }
        if (Date.now() - start > 2000) {
          resolve(false);
          return;
        }
        setTimeout(check, 50);
      };
      check();
    });
  }, []);

  useEffect(() => {
    if (loading || banners.length === 0 || useFallback) return;
    if (!carouselRef.current) return;
    if (slickInitialized.current) return;

    let cancelled = false;
    let initTimer;

    (async () => {
      const ready = await waitForSlick();
      if (cancelled || !mountedRef.current) return;

      if (!ready) {
        setUseFallback(true);
        return;
      }

      initTimer = setTimeout(() => {
        if (cancelled || !mountedRef.current || !carouselRef.current) return;
        try {
          const $c = window.$(carouselRef.current);
          if ($c.hasClass('slick-initialized')) {
            try { $c.slick('unslick'); } catch (e) { /* ignore */ }
          }

          $c.slick({
            dots: true,
            arrows: false,
            infinite: true,
            speed: 600,
            slidesToShow: 1,
            slidesToScroll: 1,
            autoplay: true,
            autoplaySpeed: SLIDE_DURATION_MS,
            fade: true,
            adaptiveHeight: true,
            pauseOnHover: false,
            pauseOnFocus: false,
          });

          slickInitialized.current = true;
        } catch (e) {
          console.warn('Slick init failed, falling back to CSS carousel', e);
          if (mountedRef.current) setUseFallback(true);
        }
      }, 0);
    })();

    return () => {
      cancelled = true;
      if (initTimer) clearTimeout(initTimer);
      if (carouselRef.current && window.$) {
        try {
          const $c = window.$(carouselRef.current);
          if ($c.hasClass('slick-initialized')) {
            $c.slick('unslick');
          }
        } catch (e) { /* ignore */ }
      }
      slickInitialized.current = false;
    };
  }, [loading, banners.length, useFallback, waitForSlick]);

  useEffect(() => {
    if (!slickInitialized.current || useFallback) return;
    if (showTrailer) {
      safeSlickCall('slickPause');
    } else {
      safeSlickCall('slickPlay');
    }
  }, [showTrailer, safeSlickCall, useFallback]);

  useEffect(() => {
    const handler = () => {
      if (document.visibilityState !== 'visible') return;
      safeSlickCall('slickPlay');
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, [safeSlickCall]);

  useEffect(() => {
    if (!useFallback || banners.length === 0) return;
    const id = setInterval(() => {
      setFallbackIndex((i) => (i + 1) % banners.length);
    }, SLIDE_DURATION_MS);
    return () => clearInterval(id);
  }, [useFallback, banners.length]);

  const renderSlideContent = (banner) => (
    <div
      className="hs-slide"
      style={{
        backgroundImage: `url(${banner.imageUrl || ""})`,
        cursor: banner.linkUrl ? "pointer" : "default",
      }}
      onClick={() => {
        if (banner.linkUrl) window.location.href = banner.linkUrl;
      }}
    >
      <div className="hs-inner">
        <div className="hs-left">
          <h1 className="hs-title">{banner.title}</h1>
          <div className="hs-meta">
            {banner.certificate && (
              <span className="hs-cert">{banner.certificate}</span>
            )}
            {banner.duration && (
              <>
                <span className="hs-sep">|</span>
                <span>{banner.duration}</span>
              </>
            )}
            {banner.language && (
              <>
                <span className="hs-sep">|</span>
                <span>{banner.language}</span>
              </>
            )}
          </div>
          {banner.description && (
            <p className="hs-desc">
              {banner.description.length > 180
                ? `${banner.description.substring(0, 180)}...`
                : banner.description}
            </p>
          )}
          {banner.linkUrl && (
            <a href={banner.linkUrl} className="hs-book-btn" onClick={(e) => e.stopPropagation()}>
              Book Now
            </a>
          )}
        </div>
        {banner.posterImage && (
          <div className="hs-right">
            <div
              className="hs-poster-wrap"
              onClick={(e) => {
                e.stopPropagation();
                if (!banner.trailerUrl) return;
                setCurrentTrailer(convertToEmbedUrl(banner.trailerUrl));
                setShowTrailer(true);
              }}
            >
              <img
                src={banner.posterImage}
                alt={banner.title}
                className="hs-poster-img"
              />
              <div className="hs-play-overlay">
                <div className="hs-play-circle">
                  <svg
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  if (loading || banners.length === 0) return null;

  return (
    <>
      <div className="hero-banner-slider">
        {useFallback ? (
          <div className="hero-banner-fallback">
            {banners.map((banner, i) => (
              <div
                key={banner.id}
                className={`hs-fb-slide ${i === fallbackIndex ? 'is-active' : ''}`}
              >
                {renderSlideContent(banner)}
              </div>
            ))}
            <div className="hs-fb-dots" role="tablist" aria-label="Banner navigation">
              {banners.map((banner, i) => (
                <button
                  key={banner.id}
                  type="button"
                  role="tab"
                  aria-label={`Go to slide ${i + 1}`}
                  aria-selected={i === fallbackIndex}
                  className={`hs-fb-dot ${i === fallbackIndex ? 'is-active' : ''}`}
                  onClick={() => setFallbackIndex(i)}
                />
              ))}
            </div>
          </div>
        ) : (
          <>
            <div ref={carouselRef} className="hero-banner-wrapper">
              {banners.map((banner) => (
                <div key={banner.id}>{renderSlideContent(banner)}</div>
              ))}
            </div>
            <button
              type="button"
              className="hs-arrow hs-arrow-prev"
              aria-label="Previous slide"
              onClick={goPrev}
              onMouseEnter={handleArrowEnter}
              onMouseLeave={handleArrowLeave}
              onFocus={handleArrowEnter}
              onBlur={handleArrowLeave}
            >
              <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path
                  d="M15 6l-6 6 6 6"
                  stroke="#ffffff"
                  strokeWidth="2.5"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <button
              type="button"
              className="hs-arrow hs-arrow-next"
              aria-label="Next slide"
              onClick={goNext}
              onMouseEnter={handleArrowEnter}
              onMouseLeave={handleArrowLeave}
              onFocus={handleArrowEnter}
              onBlur={handleArrowLeave}
            >
              <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path
                  d="M9 6l6 6-6 6"
                  stroke="#ffffff"
                  strokeWidth="2.5"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </>
        )}
      </div>

      {showTrailer && (
        <div className="hs-modal-overlay">
          <div
            className="hs-modal-backdrop"
            onClick={() => setShowTrailer(false)}
          />
          <div className="hs-modal-box">
            <button
              className="hs-modal-close"
              onClick={() => setShowTrailer(false)}
            >
              ✕
            </button>
            <iframe
              width="100%"
              height="480"
              src={currentTrailer}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          </div>
        </div>
      )}
    </>
  );
};

export default HeroSlider;
